// frontend/src/lib/api.ts (COMPLETE UPDATE)
/**
 * API Client for Digestr Backend
 * Handles all HTTP requests, authentication, and error handling
 */

import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { RedditSettings } from '@/types/settings'

import { config } from './config'
import { storage } from './utils'
import type {
    User,
    AuthResponse,
    LoginRequest,
    RegisterRequest,
    Briefing,
    BriefingRequest,
    ApiResponse,
    ApiError,
    PaginatedResponse,
    UserSettings,
} from '@/types'

interface RequestConfig extends InternalAxiosRequestConfig {
    _retry?: boolean
}

// Additional types for Sources API
interface SourceFilters {
    category?: string
    type?: string
    is_featured?: boolean
    is_premium?: boolean
    search?: string
    subscribed_only?: boolean
}

interface Source {
    id: number
    name: string
    display_name: string
    description?: string
    type: string
    category?: string
    url: string
    homepage_url?: string
    icon_url?: string
    is_active: boolean
    is_premium: boolean
    is_featured: boolean
    is_default: boolean
    is_subscribed?: boolean
    subscription_enabled?: boolean
    total_subscriptions: number
    created_at: string
    updated_at?: string
}
export type { Source };

interface UserSubscription {
    id: number
    source_id: number
    is_enabled: boolean
    priority: number
    custom_settings: any
    user_rating?: number
    articles_read: number
    articles_liked: number
    articles_disliked: number
    subscribed_at: string
    source: Source
}

interface SourceListResponse {
    sources: Source[]
    total: number
    page: number
    page_size: number
    has_more: boolean
}

interface UserSubscriptionListResponse {
    subscriptions: UserSubscription[]
    total: number
}

interface BulkSubscribeResponse {
    subscribed: number[]
    already_subscribed: number[]
    failed: number[]
    total_subscribed: number
}



interface ApiClientConfig {
    baseURL: string
    timeout: number
    withCredentials?: boolean
    headers: Record<string, string>
}


class ApiClient {
    private client: AxiosInstance
    private refreshPromise: Promise<string> | null = null
    private tokenReadyPromise: Promise<void>
    private tokenReadyResolve!: () => void
    private accessToken: string | null = null
    private config: ApiClientConfig

    constructor(config: ApiClientConfig) {
        this.config = config
        this.tokenReadyPromise = new Promise(resolve => {
            this.tokenReadyResolve = resolve
        })
        this.client = axios.create(config)
        this.setupInterceptors()
    }

    setAccessToken(token: string | null) {
        this.accessToken = token
        this.tokenReadyResolve?.()
    }

    async tryRestoreSession(): Promise<boolean> {
        try {
            const response = await axios.post<AuthResponse>(
                `${this.config.baseURL}/api/v1/auth/refresh`,
                {},
                { withCredentials: true }
            )
            const { access_token } = response.data
            if (access_token) {
                this.setAccessToken(access_token)
                return true
            }
        } catch { }
        this.setAccessToken(null)
        return false
    }

    async getCategoryPreferences(): Promise<any> {
        const response = await this.client.get('/api/v1/preferences/categories')
        return response.data
    }

    async updateCategoryPreferences(data: any): Promise<any> {
        const response = await this.client.put('/api/v1/preferences/categories', data)
        return response.data
    }

    async getCategories(): Promise<{ enabled_categories: string[], total_enabled: number }> {
        const response = await this.client.get('/api/v1/users/me/categories')
        return response.data
    }




    private setupInterceptors(): void {
        // REQUEST: attach token
        this.client.interceptors.request.use(
            async (cfg) => {  // Make it async
                await this.tokenReadyPromise
                // Add fake delay in dev for all requests
                if (import.meta.env.DEV) {
                    await new Promise(r => setTimeout(r, 2000))
                }

                if (this.accessToken) {
                    cfg.headers = cfg.headers ?? {}
                    cfg.headers.Authorization = `Bearer ${this.accessToken}`
                } else if (cfg.headers?.Authorization) {
                    delete cfg.headers.Authorization
                }

                cfg.withCredentials = true
                return cfg
            },
            (error) => Promise.reject(error)
        )

        // RESPONSE: handle 401 + refresh
        // RESPONSE: handle 401 + refresh
        this.client.interceptors.response.use(
            (response) => response,
            async (error: AxiosError) => {
                const originalRequest = error.config as RequestConfig

                // ✅ Don't redirect on 401 for polling endpoints - just fail silently
                const isPollingEndpoint = originalRequest?.url?.includes('/generation/status') ||
                    originalRequest?.url?.includes('/audio/briefing')

                const isLogoutEndpoint = originalRequest?.url?.includes('/auth/logout')

                if (error.response?.status === 401 && originalRequest) {
                    if (isPollingEndpoint || isLogoutEndpoint) {
                        return Promise.reject(error)
                    }

                    if (originalRequest._retry) {
                        this.logout()
                        window.location.href = '/auth/login'
                        return Promise.reject(error)
                    }

                    originalRequest._retry = true

                    try {
                        const newToken = await this.refreshToken()

                        if (!newToken || newToken === 'null' || newToken === 'undefined') {
                            this.logout()
                            window.location.href = '/auth/login'
                            return Promise.reject(new Error('Failed to refresh token'))
                        }

                        originalRequest.headers = originalRequest.headers ?? {}
                        originalRequest.headers.Authorization = `Bearer ${newToken}`

                        return this.client(originalRequest)
                    } catch (refreshError) {
                        console.error('🚨 Token refresh failed:', refreshError)
                        this.logout()
                        window.location.href = '/auth/login'
                        return Promise.reject(refreshError)
                    }
                }

                return Promise.reject(this.handleError(error))
            }
        )
    }



    async logout(): Promise<void> {
        await this.client.post('/api/v1/auth/logout')
        this.setAccessToken(null)
    }

    async getAudioCapabilities(): Promise<AudioCapabilities> {
        const response = await this.client.get('/api/v1/audio/capabilities')
        return response.data
    }

    async getAudioVoices(): Promise<AudioVoice[]> {
        const response = await this.client.get('/api/v1/audio/voices')
        return response.data
    }

    async getAudioStatus(briefingId: number): Promise<AudioStatus> {
        const response = await this.client.get(`/api/v1/audio/briefing/${briefingId}/status`)
        return response.data
    }

    async generateAudio(briefingId: number, forceRegenerate = false): Promise<AudioGenerateResponse> {
        const response = await this.client.post(
            `/api/v1/audio/briefing/${briefingId}/generate`,
            { force_regenerate: forceRegenerate },
            { timeout: 300000 }  // 5 minutes for audio generation
        )
        return response.data
    }

    async getAudioStats(): Promise<AudioStats> {
        const response = await this.client.get('/api/v1/audio/stats')
        return response.data
    }

    async deleteAudio(briefingId: number): Promise<void> {
        await this.client.delete(`/api/v1/audio/briefing/${briefingId}`)
    }

    async getAudioCached(): Promise<CachedAudioBriefing[]> {
        const response = await this.client.get('/api/v1/audio/cached')
        return response.data
    }


    private async refreshToken(): Promise<string> {
        if (this.refreshPromise) {
            return this.refreshPromise
        }

        this.refreshPromise = (async () => {
            try {


                const response = await this.client.post<AuthResponse>('/api/v1/auth/refresh', {

                })

                const { access_token } = response.data

                if (!access_token || access_token === 'null' || access_token === 'undefined') {
                    this.logout()
                    throw new Error('No access token in refresh response')
                }

                this.setAccessToken(access_token)
                return access_token
            } catch (error) {
                console.error('Refresh failed:', error)
                this.logout()
                throw error
            } finally {
                this.refreshPromise = null
            }
        })()

        return this.refreshPromise
    }




    private handleError(error: AxiosError): ApiError {
        if (error.response) {
            // Server responded with error status
            const { status, data } = error.response
            return {
                message: (data as any)?.message || (data as any)?.detail || 'An error occurred',
                status,
                code: (data as any)?.code,
                details: (data as any)?.details,
            }
        } else if (error.request) {
            // Network error
            return {
                message: 'Network error. Please check your connection.',
                status: 0,
                code: 'NETWORK_ERROR',
            }
        } else {
            // Request setup error
            return {
                message: error.message || 'An unexpected error occurred',
                status: 0,
                code: 'REQUEST_ERROR',
            }
        }
    }



    // ==========================================
    // AUTHENTICATION METHODS
    // ==========================================
    // Create form data for OAuth2 backend
    async login(credentials: LoginRequest): Promise<AuthResponse> {
        const formData = new URLSearchParams()
        formData.append('username', credentials.email)
        formData.append('password', credentials.password)

        const response = await this.client.post<AuthResponse>(
            '/api/v1/auth/login',
            formData,
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                withCredentials: true  // ✅ Send/receive cookies
            }
        )



        return response.data  // Just return user data
    }


    async register(userData: RegisterRequest): Promise<AuthResponse> {
        const response = await this.client.post<AuthResponse>('/api/v1/auth/register', userData)

        const { access_token, refresh_token } = response.data
        storage.set(config.tokenKey, access_token)
        if (refresh_token) {
            storage.set(config.refreshTokenKey, refresh_token)
        }

        return response.data
    }


    async loginWithGoogle(credential: string): Promise<AuthResponse> {
        const response = await this.client.post<AuthResponse>(
            '/api/v1/auth/google',
            { token: credential },
            { withCredentials: true }
        )

        const { access_token, refresh_token } = response.data
        storage.set(config.tokenKey, access_token)
        if (refresh_token) {
            storage.set(config.refreshTokenKey, refresh_token)
        }

        return response.data
    }

    async loginWithGitHub(code: string): Promise<AuthResponse> {
        const response = await this.client.post<AuthResponse>(
            '/api/v1/auth/github',
            { code },
            { withCredentials: true }
        )

        const { access_token, refresh_token } = response.data
        storage.set(config.tokenKey, access_token)
        if (refresh_token) {
            storage.set(config.refreshTokenKey, refresh_token)
        }

        return response.data
    }
    async getCurrentUser(): Promise<User> {
        const response = await this.client.get<User>('/api/v1/auth/me')
        return response.data
    }

    // ==========================================
    // USER METHODS
    // ==========================================

    async updateUser(userData: Partial<User>): Promise<User> {
        const response = await this.client.patch<ApiResponse<User>>('/api/v1/users/me', userData)
        return response.data.data
    }


    // ==========================================
    // ONBOARDING METHODS
    // ==========================================


    async getUserProfile(): Promise<User> {
        if (import.meta.env.DEV) {
            await new Promise(r => setTimeout(r, 4000))
        }
        // backend: @router.get("/me") on auth router => /api/v1/auth/me
        const response = await this.client.get<User>('/api/v1/auth/me')
        return response.data
    }

    async resendVerificationEmail(): Promise<{ message: string }> {
        // backend: @router.post("/resend-verification") => /api/v1/auth/resend-verification
        const response = await this.client.post('/api/v1/auth/resend-verification')
        return response.data
    }

    async sendWelcomeEmail(): Promise<{ message: string }> {
        const response = await this.client.post('/api/v1/users/email/welcome')
        return response.data
    }


    async completeOnboarding(): Promise<{ message: string; user: User }> {
        const response = await this.client.post('/api/v1/users/onboarding/complete')
        return response.data
    }

    async getOnboardingStatus(): Promise<{ onboarding_completed: boolean; onboarding_completed_at?: string }> {
        const response = await this.client.get('/api/v1/users/onboarding/status')
        return response.data
    }


    async skipOnboarding(): Promise<{ message: string; user: User }> {
        const response = await this.client.post('/api/v1/users/onboarding/skip')
        return response.data
    }

    // ==========================================
    // SETTINGS METHODS
    // ==========================================

    async getUserSettings(): Promise<UserSettings> {
        const response = await this.client.get<UserSettings>('/api/v1/settings/')
        return response.data
    }

    async updateUserSettings(settings: Partial<UserSettings>): Promise<UserSettings> {
        const response = await this.client.patch<UserSettings>('/api/v1/settings/', settings)
        return response.data
    }

    async resetUserSettings(): Promise<{ message: string; settings: UserSettings }> {
        const response = await this.client.post('/api/v1/settings/reset')
        return response.data
    }

    async exportSettings(): Promise<any> {
        const response = await this.client.get('/api/v1/settings/export')
        return response.data
    }

    // ==========================================
    // SOURCE MANAGEMENT METHODS
    // ==========================================

    async getSources(params?: {
        category?: string
        type?: string
        is_featured?: boolean
        search?: string
        page?: number
        page_size?: number
    }): Promise<SourceListResponse> {
        const response = await this.client.get('/api/v1/sources', { params })
        return response.data
    }

    async getSource(sourceId: number): Promise<Source> {
        const response = await this.client.get(`/api/v1/sources/${sourceId}`)
        return response.data
    }

    async getFeaturedSources(params?: {
        page?: number
        page_size?: number
    }): Promise<SourceListResponse> {
        const response = await this.client.get('/api/v1/sources/featured/list', { params })
        return response.data
    }

    async getDefaultSources(): Promise<Source[]> {
        const response = await this.client.get('/api/v1/sources/defaults/list')
        return response.data
    }

    async getUserSubscriptions(enabledOnly: boolean = true): Promise<UserSubscriptionListResponse> {
        const response = await this.client.get('/api/v1/sources/subscriptions', {
            params: { enabled_only: enabledOnly }
        })
        return response.data
    }

    async subscribeToSource(sourceId: number, priority: number = 0): Promise<UserSubscription> {
        const response = await this.client.post(
            `/api/v1/sources/${sourceId}/subscribe`,
            null,
            { params: { priority } }
        )
        return response.data
    }

    async unsubscribeFromSource(sourceId: number): Promise<{ message: string; source_id: number }> {
        const response = await this.client.delete(`/api/v1/sources/${sourceId}/subscribe`)
        return response.data
    }

    async bulkSubscribeToSources(sourceIds: number[]): Promise<BulkSubscribeResponse> {
        const response = await this.client.post('/api/v1/sources/bulk-subscribe', {
            source_ids: sourceIds
        })
        return response.data
    }

    async updateSubscriptionSettings(
        sourceId: number,
        settings: {
            is_enabled?: boolean
            priority?: number
            custom_settings?: any
            user_rating?: number
        }
    ): Promise<UserSubscription> {
        const response = await this.client.patch(
            `/api/v1/sources/${sourceId}/settings`,
            settings
        )
        return response.data
    }







    // ==========================================
    // REDDIT SETTINGS METHODS
    // ==========================================

    async getRedditSettings(): Promise<RedditSettings> {
        const response = await this.client.get('/api/v1/settings/reddit')
        return response.data
    }

    async updateRedditSettings(settings: {
        enabled?: boolean
        categories?: string[]
        custom_subreddits?: string[]
        ratio?: number
        personal_enabled?: boolean
        personal_sort?: 'hot' | 'new' | 'top' | 'best'
    }): Promise<{ message: string }> {
        const response = await this.client.patch('/api/v1/settings/reddit', settings)
        return response.data
    }






    // ==========================================
    // BRIEFING METHODS
    // ==========================================

    async getBriefings(params?: {
        page?: number
        per_page?: number
        status?: string
        category?: string
    }): Promise<any> {
        const response = await this.client.get('/api/v1/briefings', { params })
        // Backend returns: {briefings: [], total: number}
        return response.data
    }

    async getBriefing(id: string): Promise<Briefing> {
        const response = await this.client.get<Briefing>(`/api/v1/briefings/${id}`)

        return response.data
    }

    async createBriefing(briefingData: BriefingRequest): Promise<Briefing> {
        const response = await this.client.post<ApiResponse<Briefing>>('/api/v1/briefings', briefingData)
        return response.data.data
    }

    async generateBriefing(options?: {
        style?: 'comprehensive' | 'quick' | 'analytical'
        force_fresh?: boolean
    }): Promise<any> {
        const response = await this.client.post(
            '/api/v1/briefings/generate',
            options,
            { timeout: 300000 }  // 5 minutes for briefing generation
        )
        return response.data
    }

    async get(url: string, config?: any): Promise<any> {
        const response = await this.client.get(url, config);
        return response.data;
    }

    async post(url: string, data?: any, config?: any): Promise<any> {
        const response = await this.client.post(url, data, config);
        return response.data;
    }

    async put(url: string, data?: any, config?: any): Promise<any> {
        const response = await this.client.put(url, data, config);
        return response.data;
    }

    async delete(url: string, config?: any): Promise<any> {
        const response = await this.client.delete(url, config);
        return response.data;
    }

    async patch(url: string, data?: any, config?: any): Promise<any> {
        const response = await this.client.patch(url, data, config);
        return response.data;
    }




    async generateBriefingAsync(options?: {
        style?: string
        force_fresh?: boolean
    }): Promise<any> {
        const params = new URLSearchParams()
        if (options?.style) params.append('style', options.style)
        if (options?.force_fresh) params.append('force_fresh', 'true')

        const response = await this.client.post(`/api/v1/briefings/generate-async?${params.toString()}`)
        return response.data
    }

    async getBriefingStatus(jobId: string): Promise<any> {
        const response = await this.client.get(`/api/v1/briefings/status/${jobId}`)
        return response.data
    }
    async getActiveGenerationStatus(): Promise<any> {
        const response = await this.client.get('/api/v1/briefings/generation/status')
        return response.data
    }

    async deleteBriefing(id: string): Promise<void> {
        await this.client.delete(`/api/v1/briefings/${id}`)
    }

    async sendBriefing(id: string): Promise<any> {
        const response = await this.client.post(`/api/v1/briefings/${id}/send`)
        return response.data
    }

    async markBriefingAsRead(id: string | number) {
        const response = await this.client.put(`/api/v1/briefings/${id}/mark-read`)
        return response.data
    }



    async markBriefingAsUnread(id: string | number) {
        const response = await this.client.put(`/api/v1/briefings/${id}/mark-unread`)
        return response.data
    }

    async archiveBriefing(id: string): Promise<Briefing> {
        const response = await this.client.patch<ApiResponse<Briefing>>(`/api/v1/briefings/${id}/archive`)
        return response.data.data
    }

    // ==========================================
    // BILLING METHODS
    // ==========================================

    async getSubscription(): Promise<any> {
        const response = await this.client.get('/api/v1/billing/subscription')
        return response.data
    }

    async createCheckoutSession(priceId: string): Promise<{ url: string }> {
        const response = await this.client.post('/api/v1/billing/checkout', { price_id: priceId })
        return response.data
    }

    async createPortalSession(): Promise<{ url: string }> {
        const response = await this.client.post('/api/v1/billing/portal')
        return response.data
    }

    // ==========================================
    // HEALTH CHECK
    // ==========================================

    async healthCheck(): Promise<{ status: string; timestamp: string }> {
        const response = await this.client.get('/health')
        return response.data
    }

    async getDemoData(): Promise<{ user: User }> {
        return {
            user: {
                id: '1',
                email: 'demo@digestr.ai',
                full_name: 'Demo User',
                is_active: true,
                is_verified: true,
                subscription_tier: 'pro',
                subscription_status: 'active',
                stripe_customer_id: null,
                tenant_id: null,
                onboarding_completed: true,
                onboarding_completed_at: new Date().toISOString(),
                enterprise_role: null,
                briefing_categories: ['technology', 'business'],
                briefing_style: 'comprehensive',
                briefing_schedule: null,
                timezone: 'America/New_York',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                last_login: new Date().toISOString()
            }
        }
    }
}
// Create and export the API client instance
export const api = new ApiClient({
    baseURL: config.apiUrl,
    timeout: config.apiTimeout,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },

})


// ==========================================
// EXPORT INDIVIDUAL API FUNCTIONS
// ==========================================

export const auth = {
    login: (credentials: LoginRequest) => api.login(credentials),
    register: (userData: RegisterRequest) => api.register(userData),
    loginWithGoogle: (credential: string) => api.loginWithGoogle(credential),
    loginWithGitHub: (code: string) => api.loginWithGitHub(code),

    logout: async () => {
        try {
            await api.logout()
        } catch (error) {
            console.error('Logout API error:', error)
        }
        api.setAccessToken(null)
    },
    getCurrentUser: () => api.getCurrentUser(),
}
export const users = {
    update: (userData: Partial<User>) => api.updateUser(userData),

    // 👇 FIX: Added getCurrentUser here to resolve the type error in the component
    getCurrentUser: () => api.getCurrentUser(), // <- ADDED THIS LINE
    getCategories: () => api.getCategories(),
    completeOnboarding: () => api.completeOnboarding(),
    getOnboardingStatus: () => api.getOnboardingStatus(),
    skipOnboarding: () => api.skipOnboarding(),

    // 👇 NEW: use /users/me for the boolean, but return just `true | false`
    checkEmailVerified: async () => {
        const me = await api.getCurrentUser()
        return me.is_verified
    },

    // 👇 NEW: wire to the backend endpoints you just added
    resendVerificationEmail: () => api.resendVerificationEmail(),
    sendWelcomeEmail: () => api.sendWelcomeEmail(),
}





export const settings = {
    get: async (): Promise<UserSettings> => {
        const data = await api.getUserSettings()
        return {
            ...data,
            market: data.market_config ?? data.market,
        }
    },
    update: (settings: Partial<UserSettings>) => api.updateUserSettings(settings),
    reset: () => api.resetUserSettings(),
    export: () => api.exportSettings(),
}


export const redditSettings = {
    get: () => api.getRedditSettings(),
    update: (settings: Parameters<typeof api.updateRedditSettings>[0]) =>
        api.updateRedditSettings(settings)
}

export const preferences = {
    getCategories: () => api.get('/api/v1/preferences/categories'),
    updateCategories: (data: any) => api.put('/api/v1/preferences/categories', data),
    getCustomSources: () => api.get('/api/v1/preferences/custom-sources'),
    createCustomSource: (data: any) => api.post('/api/v1/preferences/custom-sources', data),
    updateCustomSource: (id: string, data: any) =>
        api.put(`/api/v1/preferences/custom-sources/${id}`, data),
    deleteCustomSource: (id: string) =>
        api.delete(`/api/v1/preferences/custom-sources/${id}`),
    testCustomSource: (data: any) =>
        api.post('/api/v1/preferences/custom-sources/test', data),
};
export const sources = {
    // List & browse
    getAll: (params?: any) => api.getSources(params),
    getById: (id: number) => api.getSource(id),
    getFeatured: (params?: any) => api.getFeaturedSources(params),
    getDefaults: () => api.getDefaultSources(),

    // User subscriptions
    getSubscriptions: (enabledOnly?: boolean) => api.getUserSubscriptions(enabledOnly),
    subscribe: (sourceId: number, priority?: number) => api.subscribeToSource(sourceId, priority),
    unsubscribe: (sourceId: number) => api.unsubscribeFromSource(sourceId),
    bulkSubscribe: (sourceIds: number[]) => api.bulkSubscribeToSources(sourceIds),
    updateSettings: (sourceId: number, settings: any) => api.updateSubscriptionSettings(sourceId, settings),
}

export const briefings = {
    getAll: (params?: any) => api.getBriefings(params),
    getById: (id: string) => api.getBriefing(id),
    create: (data: BriefingRequest) => api.createBriefing(data),
    generate: (options?: any) => api.generateBriefing(options),
    delete: (id: string) => api.deleteBriefing(id),
    send: (id: string) => api.sendBriefing(id),
    markAsRead: (id: string | number) => api.markBriefingAsRead(id),
    markAsUnread: (id: string | number) => api.markBriefingAsUnread(id),
    archive: (id: string) => api.archiveBriefing(id),

    generateAsync: (options?: { style?: string; force_fresh?: boolean }) =>
        api.generateBriefingAsync(options),
    getStatus: (jobId: string) => api.getBriefingStatus(jobId),
    getActiveGenerationStatus: () => api.getActiveGenerationStatus()

}

export const billing = {
    getSubscription: () => api.getSubscription(),
    createCheckoutSession: (priceId: string) => api.createCheckoutSession(priceId),
    createPortalSession: () => api.createPortalSession(),
}

// ============== TYPES ==============

export interface AudioCapabilities {
    enabled: boolean;
    tier: string;
    model: string;
    max_per_day: number;
    today_count: number;
    remaining_today: number;
    available_formats: string[];
    available_voices: string[];
}

export interface AudioVoice {
    id: string;
    name: string;
    description: string;
    gender: string;
    style: string;
}


export interface CachedAudioBriefing {
    id: number;
    title: string;
    duration_seconds: number;
    voice: string;
    generated_at: string;
    created_at: string;
}




export interface AudioStatus {
    has_audio: boolean;
    audio_url: string | null;
    duration_seconds: number | null;
    format: string | null;
    voice: string | null;
    generated_at: string | null;
}

export interface AudioGenerateResponse {
    success: boolean;
    message: string;
    audio_url: string;
    duration_seconds: number;
    duration_minutes: number;
    format: string;
    voice: string;
    cost_usd: number;
}

export interface AudioStats {
    total_audio_briefings: number;
    total_duration_minutes: number;
    total_cost_usd: number;
    today_count: number;
    avg_duration_minutes: number;
    avg_cost_usd: number;
}

// ============== API METHODS ==============

export const audioApi = {
    getCapabilities: () => api.getAudioCapabilities(),
    getCachedBriefings: () => api.getAudioCached(),
    getVoices: () => api.getAudioVoices(),
    getStatus: (briefingId: number) => api.getAudioStatus(briefingId),
    generate: (briefingId: number, forceRegenerate?: boolean) => api.generateAudio(briefingId, forceRegenerate),
    getStats: () => api.getAudioStats(),
    delete: (briefingId: number) => api.deleteAudio(briefingId),


    getStreamUrl: (briefingId: number) => {
        const token = document.cookie.split("; ").find(r => r.startsWith("access_token="))?.split("=")[1]
        const params = token ? `?token=${encodeURIComponent(token)}` : ""
        return `/api/v1/audio/briefing/${briefingId}/stream${params}`
    },
    getDownloadUrl: (briefingId: number) => {
        return `/api/v1/audio/briefing/${briefingId}/download`;
    },
};


// Use your existing API_URL constant
const API_URL = import.meta.env.VITE_API_URL || '';

